/**
 * 팀 전문분야 관련 유틸리티 함수
 *
 * - 프로젝트 대분류와 팀 주직무(전문분야) 통일
 * - DB enum과 코드 간 변환 로직 중앙화
 */

import { MAIN_CATEGORIES } from '@/constants/project-categories'

/** MAIN_CATEGORIES는 {value,label}[] 이므로 value만 사용 */
export const TEAM_MAIN_CATEGORY_VALUES = MAIN_CATEGORIES.map((c) => c.value) as readonly string[]

/** 팀 전문분야는 "문자열"로만 유지 (객체 금지) */
export type TeamSpecialty = string

/** 프로젝트 대분류 + 기타 */
export const TEAM_SPECIALTY_OPTIONS: readonly TeamSpecialty[] = [...TEAM_MAIN_CATEGORY_VALUES, '기타']

/** 영어 코드 값 → 한글 값(프로젝트 대분류 문자열) 매핑 */
export const ENGLISH_SPECIALTY_TO_NEW: Record<string, TeamSpecialty> = {
  WEB: '웹 개발',
  MOBILE: '모바일 앱 개발',
  DESIGN: '디자인/미디어',
  MARKETING: '마케팅/기타',
  DATA: '인공지능(AI)/데이터',
  AI: '인공지능(AI)/데이터',
  BLOCKCHAIN: '블록체인',
  GAME: '게임/VR/AR',
  ETC: '기타',
}

/** 레거시 값 → 새 값(프로젝트 대분류 문자열) 매핑 */
export const LEGACY_SPECIALTY_TO_NEW: Record<string, TeamSpecialty> = {
  '웹/앱 프로덕트 개발': '웹 개발',
  '웹 및 모바일 개발': '웹 개발',
  'AI / 데이터 솔루션': '인공지능(AI)/데이터',
  '데이터 및 인공지능': '인공지능(AI)/데이터',
  'IT 인프라 / DevOps': '인프라/클라우드/보안',
  '클라우드 및 인프라': '인프라/클라우드/보안',
  '보안 및 테스트': '인프라/클라우드/보안',
  '디자인 / 브랜딩': '디자인/미디어',
  '기획 / 마케팅 / 컨설팅': '기획/PM',
  '비즈니스 프로세스 관리': '기획/PM',
  기타: '기타',
}

/**
 * 새 값 → DB enum 값 매핑
 * (현재는 프로젝트 대분류를 그대로 enum으로 저장하는 정책이라 1:1)
 */
export const NEW_SPECIALTY_TO_ENUM: Record<string, string> = TEAM_SPECIALTY_OPTIONS.reduce(
  (acc, v) => {
    acc[v] = v
    return acc
  },
  {} as Record<string, string>,
)

/**
 * 화면 표시용 라벨 반환
 * - 레거시가 들어오면 프로젝트 대분류로 변환
 * - 알 수 없는 값은 원본 그대로
 */
export function getSpecialtyLabel(value: string): string {
  return LEGACY_SPECIALTY_TO_NEW[value] ?? value
}

/**
 * specialty 값을 enum 값으로 변환 (DB 저장용)
 * - 이미 enum(=대분류 문자열) 이면 그대로
 * - 영어 코드 값이면 한글로 변환
 * - 레거시면 변환 후 저장
 */
export function convertSpecialtyToEnum(specialties: string[]): string[] {
  return specialties.map((spec) => {
    // 1. 이미 올바른 enum 값인지 확인
    const direct = NEW_SPECIALTY_TO_ENUM[spec]
    if (direct) return direct

    // 2. 영어 코드 값인지 확인 (DESIGN, WEB, MOBILE 등)
    const englishMapped = ENGLISH_SPECIALTY_TO_NEW[spec]
    if (englishMapped && NEW_SPECIALTY_TO_ENUM[englishMapped]) {
      return NEW_SPECIALTY_TO_ENUM[englishMapped]
    }

    // 3. 레거시 한글 값인지 확인
    const mapped = LEGACY_SPECIALTY_TO_NEW[spec]
    if (mapped && NEW_SPECIALTY_TO_ENUM[mapped]) return NEW_SPECIALTY_TO_ENUM[mapped]

    // 4. 변환 불가능한 값은 그대로 반환 (에러 발생 가능)
    return spec
  })
}
